"use client"

/**
 * The membership-first site's mega menu (see `./navbar-membership-first.tsx`):
 * the Check-ups menu as three text-first columns. Today's menus, How it works
 * with its cards and videos and What we offer with the membership card, stay
 * in `./mega-menu.tsx` until launch.
 */

import { useLocale } from "next-intl"
import { blobMediaUrl } from "@/lib/blob-url"
import { useTranslations } from "@/lib/intl/use-translations"
import Link from "next/link"
import Image from "next/image"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { useCountry } from "@/components/layout/country-provider"
import { useLivePrices } from "@/lib/use-live-prices"
import { membershipTierAmount } from "@/lib/pricing"
import { scanOnTopOfPlan } from "@/lib/pricing/member-prices"
import { formatCurrency } from "@/lib/format"
import { isComingSoon } from "@/i18n/config"
import { isServicePathHidden } from "@/lib/service-path-visibility"
import { ComingSoonPill } from "@/components/ui/coming-soon-pill"
import { baseBloodTest } from "@/lib/base-blood-test"
import type { NavbarItem } from "./types"
import { isMembershipFirstSite } from "@/lib/membership-first"
import { MEMBERSHIP_FIRST_BLOOD_ADDONS } from "@/data/membership-first-blood-addons"

/**
 * The imaging add-ons hardcoded in the imaging column below, as bare
 * `/tests/<slug>` paths. The "Additional scans" heading is only worth
 * rendering while at least one of these links survives this market's CMS
 * service state and page gates.
 */
const IMAGING_ADD_ON_PATHS = [
  "/tests/calcium-score",
  "/tests/body-composition",
  "/tests/dexa-scan",
  "/tests/brain-volumetry",
  "/tests/mamma-mri",
  "/tests/prostate-mri",
  "/tests/pelvic-mri",
]

/** The blood add-ons hardcoded in the blood column, same rule as imaging. */
const BLOOD_ADD_ON_PATHS = [
  "/tests/essential-b-vitamins-test",
  "/tests/hormone-blood-test-women",
  "/tests/hormone-blood-test-men",
  "/tests/omega-blood-test",
  "/tests/lpa",
  "/tests/cortisol-blood-test",
  "/tests/psa-blood-test",
]

export function DropdownMenu({
  items,
}: {
  items: { label: string; href: string }[]
}) {
  return (
    <div className="absolute top-full left-1/2 z-50 mt-1 min-w-[180px] -translate-x-1/2 rounded-[12px] border border-neutral-100 bg-white py-1 shadow-md">
      {items.map((item) => (
        <Link
          key={item.href}
          href={item.href}
          className="hover:text-aubergine-900 block px-4 py-2 text-sm text-neutral-700 transition-colors hover:bg-neutral-50"
        >
          {item.label}
        </Link>
      ))}
    </div>
  )
}

export function WhatWeOfferMegaMenu({
  navHiddenPaths = [],
  megaItem,
}: {
  megaItem?: NavbarItem
  /** Bare `/tests/<slug>` paths hidden from the menu for this country (CMS
   *  `products.marketStatus[].hideFromNav`). */
  navHiddenPaths?: string[]
}) {
  const t = useTranslations("nav")
  const ut = useTranslations("ui")
  const localePath = useLocalePath()
  const locale = useLocale()
  const { code: country } = useCountry()
  const prices = useLivePrices(country)
  const mriComingSoon = isComingSoon(country, "mri")
  // The MRI as /pricing prices it, on top of the membership: from the plain
  // full-body MRI (Core), the lowest of the three packages (Eugene, 12.10).
  const mriOnTop = scanOnTopOfPlan(
    prices.prices,
    "core",
    membershipTierAmount(prices.prices, "once", country)
  )
  const mriFrom =
    mriOnTop != null && mriOnTop > 0 ? formatCurrency(mriOnTop, country) : ""
  const isNavHidden = (path: string) =>
    isServicePathHidden({ path, country, locale, navHiddenPaths })
  // The market's base blood check-up: `prevention-starter` (Vorsorge-Starter)
  // on DE, `advanced-blood-test` everywhere else, as the nav hook and footer
  // link it. DE hides and 308-redirects the Advanced blood test.
  const baseBlood = baseBloodTest(country)
  // Austria's standalone blood tests are all hidden (its blood work is bundled
  // into the MRI packages), so the heading follows the links, as for imaging.
  const showBloodAddOns = BLOOD_ADD_ON_PATHS.some((path) => !isNavHidden(path))
  // Imaging add-ons are hidden on DE until MRI launches there (mirrors the
  // lean DE footer + nav), and each one can still be pulled on its own by CMS
  // service state or its page gate, so the heading follows the links.
  const showImagingAddOns =
    // country-gate-ignore: launch-wave hold on the imaging family, not a gate
    country !== "de" && IMAGING_ADD_ON_PATHS.some((path) => !isNavHidden(path))
  // Germany sells blood only, so every German imaging link is hidden by now;
  // the column is left out then rather than rendered empty.
  const showImagingColumn =
    !isNavHidden("/tests/full-body-mri") || showImagingAddOns
  // Three columns as wide as their content, the same gap between each
  // (Eugene, 29.09). The imaging column keeps the last 30 % of the row, where
  // the old equal thirds put it, and the free space splits evenly between the
  // other two. No left padding: on a 1440 screen the first column's text
  // starts right under "How it works".
  return (
    <div className="ml-auto max-w-[1100px] pt-6 pr-8 pb-8">
      <div className="grid grid-cols-[max-content_max-content_30%] items-start justify-between gap-x-16">
        {/* First column: check-up information, text only (Eugene, 17.09),
            a step above the 16 px items beside it rather than 22 px (29.09). */}
        <div className="flex flex-col gap-1">
          {megaItem?.children
            ?.filter(
              // The hrefs come localized ("/pathologies-risques" in French),
              // so match them the same way (audit, 09.10).
              (child) =>
                child.href.endsWith(localePath("/what-we-detect")) ||
                child.href.endsWith(localePath("/biomarkers")) ||
                child.href.endsWith(localePath("/report"))
            )
            .map((child) => (
              <Link
                key={child.href}
                href={child.href}
                className="type-body-xl text-foreground rounded-[12px] px-2 py-2 font-medium whitespace-pre-line hover:bg-white/70"
              >
                {child.label}
              </Link>
            ))}
          {!isNavHidden("/pricing") && (
            <Link
              href={localePath("/pricing")}
              className="type-body-xl text-foreground rounded-[12px] px-2 py-2 font-medium hover:bg-white/70"
            >
              {t("pricing")}
            </Link>
          )}
        </div>
        {/* Blood tests precede imaging in every market. */}
        <div>
          {!isNavHidden(`/tests/${baseBlood.slug}`) && (
            <Link
              href={localePath(`/tests/${baseBlood.slug}`)}
              className="group flex items-center gap-4 rounded-[12px] p-2 hover:bg-white/70"
            >
              <div className="h-[58px] w-[58px] flex-shrink-0 overflow-hidden rounded-[8px]">
                <Image
                  src={blobMediaUrl("/images/mega-menu/tube-advanced.webp")}
                  alt=""
                  width={116}
                  height={116}
                  className="h-[58px] w-[58px] object-cover transition-transform duration-300 ease-out group-hover:scale-[1.08]"
                />
              </div>
              <div>
                <p className="type-title-md text-foreground">
                  {t(baseBlood.labelKey)}
                </p>
                {/* The price row belongs to the Advanced blood test. DE's
                    Vorsorge-Starter has no Stripe price in the feed and its
                    page hides EUR prices, so it gets none (hide before you
                    mislabel). */}
                {baseBlood.slug === "advanced-blood-test" &&
                  t("fromChf299", {
                    price: prices.fullPrices.blood ?? "",
                  }) && (
                    <p className="type-body-sm text-foreground">
                      {t("fromChf299", {
                        price: prices.fullPrices.blood ?? "",
                      })}
                    </p>
                  )}
              </div>
            </Link>
          )}
          {showBloodAddOns && (
            <p className="type-label-md mt-4 mb-2 px-2 text-neutral-600">
              {t("additionalBloodTests")}
            </p>
          )}
          <div className="flex flex-col gap-1">
            {!isNavHidden("/tests/essential-b-vitamins-test") && (
              <Link
                href={localePath("/tests/essential-b-vitamins-test")}
                className="type-title-md text-foreground rounded-[8px] px-2 py-1 hover:bg-white/70"
              >
                {t("bVitaminsShort")}
              </Link>
            )}
            {!isNavHidden("/tests/hormone-blood-test-women") && (
              <Link
                href={localePath("/tests/hormone-blood-test-women")}
                className="type-title-md text-foreground rounded-[8px] px-2 py-1 hover:bg-white/70"
              >
                {t("hormonesWomenShort")}
              </Link>
            )}
            {!isNavHidden("/tests/hormone-blood-test-men") && (
              <Link
                href={localePath("/tests/hormone-blood-test-men")}
                className="type-title-md text-foreground rounded-[8px] px-2 py-1 hover:bg-white/70"
              >
                {t("hormonesMenShort")}
              </Link>
            )}
            {!isNavHidden("/tests/omega-blood-test") && (
              <Link
                href={localePath("/tests/omega-blood-test")}
                className="type-title-md text-foreground rounded-[8px] px-2 py-1 hover:bg-white/70"
              >
                {t("omega36Short")}
              </Link>
            )}
            {!isNavHidden("/tests/lpa") && (
              <Link
                href={localePath("/tests/lpa")}
                className="type-title-md text-foreground rounded-[8px] px-2 py-1 hover:bg-white/70"
              >
                {t("lpaShort")}
              </Link>
            )}
            {!isNavHidden("/tests/cortisol-blood-test") && (
              <Link
                href={localePath("/tests/cortisol-blood-test")}
                className="type-title-md text-foreground rounded-[8px] px-2 py-1 hover:bg-white/70"
              >
                {t("cortisolShort")}
              </Link>
            )}
            {!isNavHidden("/tests/psa-blood-test") && (
              <Link
                href={localePath("/tests/psa-blood-test")}
                className="type-title-md text-foreground rounded-[8px] px-2 py-1 hover:bg-white/70"
              >
                {t("psaShort")}
              </Link>
            )}
            {/* The membership-first site's own blood tests. */}
            {isMembershipFirstSite(country) &&
              MEMBERSHIP_FIRST_BLOOD_ADDONS.filter(
                (addon) => !isNavHidden(`/tests/${addon.slug}`)
              ).map((addon) => (
                <Link
                  key={addon.slug}
                  href={localePath(`/tests/${addon.slug}`)}
                  className="type-title-md text-foreground rounded-[8px] px-2 py-1 hover:bg-white/70"
                >
                  {t(addon.navKey)}
                </Link>
              ))}
          </div>
        </div>

        {/* Imaging */}
        {showImagingColumn && (
          <div>
            {!isNavHidden("/tests/full-body-mri") && (
              <Link
                href={localePath("/tests/full-body-mri")}
                className="group flex items-center gap-4 rounded-[12px] p-2 hover:bg-white/70"
              >
                <div className="h-[58px] w-[58px] flex-shrink-0 overflow-hidden rounded-[8px]">
                  <Image
                    src={blobMediaUrl("/images/mega-menu/mri-advanced.webp")}
                    alt=""
                    width={116}
                    height={116}
                    className="h-[58px] w-[58px] object-cover transition-transform duration-300 ease-out group-hover:scale-[1.08]"
                  />
                </div>
                <div>
                  <p className="type-title-md text-foreground">
                    {t("fullBodyMri")}
                  </p>
                  {/* Coming-soon markets have no MRI price — the pill takes
                        the price line instead of crowding the title row. */}
                  {mriComingSoon ? (
                    <div className="mt-1">
                      <ComingSoonPill
                        label={ut("comingSoon")}
                        variant="light"
                      />
                    </div>
                  ) : (
                    // The full-body MRI, Core, on top of the plan.
                    mriFrom && (
                      <p className="type-body-sm text-foreground">
                        {t("fromChf1990", { price: mriFrom })}
                      </p>
                    )
                  )}
                </div>
              </Link>
            )}
            {showImagingAddOns && (
              <>
                <p className="type-label-md mt-4 mb-2 px-2 text-neutral-600">
                  {t("additionalScans")}
                </p>
                <div className="flex flex-col gap-1">
                  {!isNavHidden("/tests/calcium-score") && (
                    <Link
                      href={localePath("/tests/calcium-score")}
                      className="type-title-md text-foreground rounded-[8px] px-2 py-1 hover:bg-white/70"
                    >
                      {t("calciumScore")}
                    </Link>
                  )}
                  {!isNavHidden("/tests/body-composition") && (
                    <Link
                      href={localePath("/tests/body-composition")}
                      className="type-title-md text-foreground rounded-[8px] px-2 py-1 hover:bg-white/70"
                    >
                      {t("bodyCompositionScore")}
                    </Link>
                  )}
                  {!isNavHidden("/tests/dexa-scan") && (
                    <Link
                      href={localePath("/tests/dexa-scan")}
                      className="type-title-md text-foreground rounded-[8px] px-2 py-1 hover:bg-white/70"
                    >
                      {t("dexaScan")}
                    </Link>
                  )}
                  {!isNavHidden("/tests/brain-volumetry") && (
                    <Link
                      href={localePath("/tests/brain-volumetry")}
                      className="type-title-md text-foreground rounded-[8px] px-2 py-1 hover:bg-white/70"
                    >
                      {t("brainAnalysis")}
                    </Link>
                  )}
                  {!isNavHidden("/tests/mamma-mri") && (
                    <Link
                      href={localePath("/tests/mamma-mri")}
                      className="type-title-md text-foreground rounded-[8px] px-2 py-1 hover:bg-white/70"
                    >
                      {t("mammaMri")}
                    </Link>
                  )}
                  {!isNavHidden("/tests/prostate-mri") && (
                    <Link
                      href={localePath("/tests/prostate-mri")}
                      className="type-title-md text-foreground rounded-[8px] px-2 py-1 hover:bg-white/70"
                    >
                      {t("prostateMri")}
                    </Link>
                  )}
                  {!isNavHidden("/tests/pelvic-mri") && (
                    <Link
                      href={localePath("/tests/pelvic-mri")}
                      className="type-title-md text-foreground rounded-[8px] px-2 py-1 hover:bg-white/70"
                    >
                      {t("pelvicMri")}
                    </Link>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
